import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, retry, switchMap, throwError, timeout, timer } from 'rxjs';
import {description} from 'platform'

import { environment } from 'src/environments/environment';
import Constants from '../constants/constants';

import { NgxSpinnerService } from 'ngx-spinner';

import { SessionService } from './session.service';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root',
})
export class RestAPIsService {
  // private internetIssue: any = 'Ooops! You are not connected internet. Please check.';
  // Domestic API
  private domesticAPIUrl: string = environment.domesticAPIUrl;

  // Business API
  private businessAPIUrl: string = environment.businessAPIUrl;

  // Prod API
  private prodAPIUrl: string = environment.prodAPIUrl;

  constructor(
    private http: HttpClient,
    private spinner: NgxSpinnerService,
    private sessionService: SessionService,
    private util: UtilService
  ) { }

  // GET API
  // getAPI(path: string, condition?: string): Observable<any> {
  //   return this.http.get((condition === 'domestic' ? this.domesticAPIUrl : (condition === 'other') ? '' : this.businessAPIUrl) + path)
  //     .pipe(retry(1), timeout(30000), catchError((error: HttpErrorResponse) => {
  //       this.sendErrorData('GET', {}, error);
  //       return throwError(() => error);
  //     }));
  // }

  getAPI(path: string, condition?: string): Observable<any> {
    const url = (condition === 'domestic' ? this.domesticAPIUrl : 
                 (condition === 'other') ? '' : this.businessAPIUrl) + path;

    let attempts = 0;
    const maxRetries = 1; // Set max retry attempts

    const makeRequest = (): Observable<any> => {
      return this.http.get(url).pipe(
        timeout(30000), // Apply timeout for each request
        catchError((error: HttpErrorResponse) => {
          if (attempts < maxRetries) {
            attempts++;
            return timer(500).pipe( 
              switchMap(() => makeRequest()) // Recursively retry with timeout
            );
          } else {
            this.sendErrorData(url, 'GET', {}, error);
            return throwError(() => error);
          }
        })
      );
    };

    return makeRequest(); // Start the API call
  }


  // POST API
  postAPI(path: string, body: any, condition?: string, isMultipart = false): Observable<any> {
    let httpOptions: Object = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    const url = (condition === 'domestic' ? this.domesticAPIUrl : (condition === 'prod') ? this.prodAPIUrl : this.businessAPIUrl) + path
    return this.http.post(url,
      body,
      isMultipart ? {} : httpOptions
    ).pipe(retry(1), timeout(40000), catchError((error: HttpErrorResponse) => {
      this.sendErrorData(url, 'POST', body, error);
      return throwError(() => error);
    }));
  }

  // POST API for download PDF
  postAPIForPdf(path: string, body: any, condition?: string, responseType = 'blob'): Observable<any> {
    let httpOptions: Object = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType
    };
    const url = (condition === 'domestic' ? this.domesticAPIUrl : this.businessAPIUrl) + path
    return this.http.post(url,
      body,
      httpOptions
    ).pipe(retry(1), timeout(30000), catchError((error: HttpErrorResponse) => {
      this.sendErrorData(url, 'POST', body, error);
      return throwError(() => error);
    }));
  }

  // POST API
  putAPI(path: string, body: any, condition?: string): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    const url = (condition === 'domestic' ? this.domesticAPIUrl : this.businessAPIUrl) + path;
    return this.http.put(url,
      body,
      httpOptions
    ).pipe(retry(1), timeout(30000), catchError((error: HttpErrorResponse) => {
      this.sendErrorData(url, 'PUT', body, error);
      return throwError(() => error);
    }));
  }

  // POST API
  deleteAPI(path: string, condition?: string): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    const url = (condition === 'domestic' ? this.domesticAPIUrl : this.businessAPIUrl) + path;
    return this.http.get(url,
      httpOptions
    ).pipe(retry(1), timeout(30000), catchError((error: HttpErrorResponse) => {
      this.sendErrorData(url, 'DELETE', {}, error);
      return throwError(() => error);
    }));
  }

  // PATCH API
  patchAPI(path: string, body: any, condition?: string): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    const url = (condition === 'domestic' ? this.domesticAPIUrl : this.businessAPIUrl) + path;
    return this.http.patch(url,
      body,
      httpOptions
    ).pipe(retry(1), timeout(30000), catchError((error: HttpErrorResponse) => {
      this.sendErrorData(url, 'PATCH', body, error);
      return throwError(() => error);
    }));
  }

  /**
   * 
   * @param {string} path URL for the API
   * @param {string} condition Condition to use domestic API or business API
   * @returns {Observable}
   */
  getAPIForPdf(path: string, condition?: string): Observable<any> {
    let httpOptions: Object = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType: 'blob',
    };
    const url = (condition === 'domestic' ? this.domesticAPIUrl : this.businessAPIUrl) + path;
    return this.http
      .get(
        url,
        httpOptions
      )
      .pipe(retry(1), timeout(30000), catchError((error: HttpErrorResponse) => {
        this.sendErrorData(url, 'GET', {}, error);
        return throwError(() => error);
      }));
  }

  /**
   * @description Get the GeoTiff data
   * @param { string } path URL for the API 
   * @returns { Observable }
   */
  getGeoTiffData(path: string): Observable<Blob> {
    const url = environment.domesticAPIUrl + path;
    return this.http.get(url, { responseType: 'blob' }).pipe(catchError((error: HttpErrorResponse) => {
      this.sendErrorData(url, 'GET', {}, error);
      return throwError(() => error);
    }));
  }

  // Generic user activity
  userActivity(payload: any): void {
    if (!!this.sessionService.getItemFromSession(Constants.sessionConstants.CORRELATIONID)) {
      payload['correlationId'] = this.sessionService.getItemFromSession(Constants.sessionConstants.CORRELATIONID);
    }
    this.postAPI('carbon/v1/add-user-data', payload, 'domestic').subscribe({
      next: (data) => {
        // Store into session
        this.sessionService.setItemIntoSession(Constants.sessionConstants.CORRELATIONID, data?.correlationId);
      },
      error: (err) => console.log('ERROR: ', err)
    })
  }

  // Generic user activity after login
  addCheckpoint(event: string): void {
    const payload = {
      email: this.sessionService.getItemFromSession(Constants.sessionConstants.COMPANY_SITE_DATA)?.contactEmail,
      event: event,
      siteId: this.sessionService.getItemFromSession(Constants.sessionConstants.COMPANY_SITE_DATA)?.siteId,
    };

    this.postAPI('carbon/v3/add-checkpoint', payload, 'domestic').subscribe({
      next: (data) => { },
      error: (err) => {
          // this.sendErrorData('POST', payload, err);
      }
    });
  }

  getQueryParams(url:string) {
    try {
      const params = new URL(url).searchParams;
      const queryParams: Record<string, string> = {};
    
      params.forEach((value, key) => {
        queryParams[key] = value;
      });
      return queryParams;
    } catch (error) {
      return {};
    }
  }

  sendErrorData(reqUrl: string, method:string, body = {}, error:HttpErrorResponse){
    const queryParam = error.url || reqUrl ? this.getQueryParams(error.url || reqUrl) : {};
    queryParam['utmSource'] = this.sessionService.getItemFromSession(Constants.CONFIG.INFO)?.source;
    const errorData = {
      source: 'UI',
      requestMethod: method,
      requestUrl: error.url || reqUrl,
      statusCode: error.status,
      errorMessage: error.message,
      requestBody: {...body, ...queryParam},
      responseBody: error.error,
      clientInfo: {
        browser: description,
        screen: `${window.screen.width}x${window.screen.height}`,
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href,
      },
      subject: this.getErrorSubject(error, reqUrl),
      apiName: this.getAPIName(this.getPathName(error.url || reqUrl))
    };

    const url = `${this.domesticAPIUrl}carbon/v3/api-error-log`;
    const option: RequestInit = {
      body: JSON.stringify(errorData),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'e_api_key': environment.resource_identifier
      },
    }
    // Keep this code as it is to avoid JWT interceptor for this API
    fetch(url, option).then(response => response.json()).then(data => {}).catch(error => {});

  }

  getErrorSubject(error:HttpErrorResponse, reqUrl: string): string {
    let subject = `Failure Report [${environment.envName} - LCR] : ${this.getAPIName(this.getPathName(error.url || reqUrl))} API Fails, `;
    if (error.error) {
      if(error.error.message) {
        subject += error.error.message;
      } else if (error.error.error) {
        if(error.error.error[0]?.type == "SITE_EXIST") {
          subject += error.error.error[0].type;
        }
        else{
          subject += error.error.error[0].message;
        }
      }
    }
    else if (error.message) {
      subject += error.message;
    }
    return subject;
  }

  getPathName(url: string | null):string {
    if (!url) {
      return '';
    }
    url = this.sanitizeSensitiveUrl(url);
    const parsedUrl = new URL(url);
    return parsedUrl.pathname.replace(/^\/+/, ''); // Remove leading slash
  }

  getAPIName(pathName: string):string {
    if (!pathName) {
      return '';
    }
    return pathName.replace('carbon/v3/', '').replace('carbon/v2/', '').replace('carbon/v1/', '');
  }

  sanitizeSensitiveUrl(url: string): string {
    return url.replace(/\/([A-Za-z0-9\-_=]{30,})/g, '/');
  }

}